import { Box, useMantineTheme } from '@mantine/core';
import { keyframes } from '../utils/keyframes';
import { forwardRef, useState, useEffect } from 'react';
import { useReducedMotion } from '../hooks';

export type RevealDirection = 'left' | 'right' | 'top' | 'bottom' | 'center';

export interface PageRevealProps {
  children: React.ReactNode;
  visible?: boolean;
  direction?: RevealDirection;
  duration?: number;
  color?: string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  onRevealComplete?: () => void;
}

/**
 * Reveals page content with a colored overlay animation
 * Great for dramatic page entrances
 */
const PageReveal = forwardRef<HTMLDivElement, PageRevealProps>(
  (
    {
      children,
      visible = true,
      direction = 'left',
      duration = 600,
      color,
      gradientVariant = 'primary',
      onRevealComplete,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const reducedMotion = useReducedMotion();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const overlayColor = color || gradient?.from || theme.primaryColor;

    const [phase, setPhase] = useState<'initial' | 'cover' | 'reveal' | 'done'>('initial');

    const actualDuration = reducedMotion ? 0 : duration / 2;

    useEffect(() => {
      if (visible && phase === 'initial') {
        setPhase('cover');

        const coverTimer = setTimeout(() => {
          setPhase('reveal');

          const revealTimer = setTimeout(() => {
            setPhase('done');
            onRevealComplete?.();
          }, actualDuration);

          return () => clearTimeout(revealTimer);
        }, actualDuration);

        return () => clearTimeout(coverTimer);
      }
    }, [visible, phase, actualDuration, onRevealComplete]);

    const getOverlayStyle = (): React.CSSProperties => {
      const base: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: overlayColor,
        zIndex: 10,
        transition: reducedMotion ? 'none' : `transform ${actualDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
      };

      const transforms = {
        initial: {
          left: { transform: 'translateX(-100%)' },
          right: { transform: 'translateX(100%)' },
          top: { transform: 'translateY(-100%)' },
          bottom: { transform: 'translateY(100%)' },
          center: { transform: 'scale(0)', borderRadius: '50%' },
        },
        cover: {
          left: { transform: 'translateX(0)' },
          right: { transform: 'translateX(0)' },
          top: { transform: 'translateY(0)' },
          bottom: { transform: 'translateY(0)' },
          center: { transform: 'scale(1.5)', borderRadius: '0%' },
        },
        reveal: {
          left: { transform: 'translateX(100%)' },
          right: { transform: 'translateX(-100%)' },
          top: { transform: 'translateY(100%)' },
          bottom: { transform: 'translateY(-100%)' },
          center: { transform: 'scale(0)', borderRadius: '50%' },
        },
        done: {
          left: { transform: 'translateX(100%)', opacity: 0 },
          right: { transform: 'translateX(-100%)', opacity: 0 },
          top: { transform: 'translateY(100%)', opacity: 0 },
          bottom: { transform: 'translateY(-100%)', opacity: 0 },
          center: { transform: 'scale(0)', opacity: 0 },
        },
      };

      return { ...base, ...transforms[phase][direction] };
    };

    return (
      <Box ref={ref} style={{ position: 'relative', overflow: 'hidden' }}>
        <Box
          style={{
            opacity: phase === 'done' || phase === 'reveal' ? 1 : 0,
            transition: reducedMotion ? 'none' : `opacity ${actualDuration / 2}ms ease`,
          }}
        >
          {children}
        </Box>

        {phase !== 'done' && (
          <Box style={getOverlayStyle()} />
        )}
      </Box>
    );
  }
);

PageReveal.displayName = 'PageReveal';

export default PageReveal;
